import { useEffect, useRef, useState } from 'react';

export function useSnackbarHoverProgressTimer(
  durationMs: number | null = 4000,
  onComplete?: () => void
) {
  const [progress, setProgress] = useState(100);
  const isHoveredRef = useRef(false);
  const startTimeRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const completedRef = useRef(false);

  const tick = (timestamp: number) => {
    if (completedRef.current || isHoveredRef.current) return;

    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const pct = Math.max(100 - (elapsed / durationMs!) * 100, 0);
    setProgress(pct);

    if (pct > 0) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      completedRef.current = true;
      onComplete?.();
    }
  };

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (!completedRef.current) {
      completedRef.current = true;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      onComplete?.();
    }
  };

  return {
    progress,
    handleMouseEnter,
    handleMouseLeave,
  };
}
